import { useState } from 'react';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import Stock from '../../../model/Stock';
import { TopHoldersResult } from '../../../model/TopHoldersResult';
import { RegisterListingHolder, RegisterListingResult } from '../../../model/RegisterListingResult';
import api from '../../../api/api';
import { to } from '../../../util/util';

// Share Registry › Reports (Figma 208:3978) and the printable reports it makes (/registry-report/{ticker:exchange}/{key}).
// Every number is worked out in the browser from the two GETs the Top holders / All holders tabs already use:
// /api/ShareRegistry/topHolders (the provider's top 50 report) and /api/ShareRegistry/registerListing (every holder,
// with each one's balance 30 days ago). Things with no source yet (report history, the monthly email, substantial-holder
// notices, announcement and email results, the share price) show "—" or a "Not connected yet" note.

export type ReportKey = 'top-20' | 'holders-by-size' | 'who-bought-and-sold' | 'substantial-holders' | 'board-pack';

export interface ReportDef { key: ReportKey; title: string; what: string; why: string }

export const REPORTS: ReportDef[] = [
    { key: 'top-20', title: 'Top 20 shareholders', what: 'Who owns the most shares, with the percentage each one holds.', why: 'You need this for the annual report and most board packs.' },
    { key: 'holders-by-size', title: 'Holders by size', what: 'How many people hold 1–1,000 shares, 1,001–5,000, and so on.', why: 'The ASX calls this a distribution schedule. It goes in the annual report.' },
    { key: 'who-bought-and-sold', title: 'Who bought and sold', what: 'Everyone whose holding changed over the last 30 days.', why: 'Good for spotting a new investor building a position.' },
    { key: 'substantial-holders', title: 'Substantial holders (5%+)', what: 'Anyone holding 5% or more, and how much they hold.', why: 'These holders must lodge a form with the ASX — this is how you keep track.' },
];

export const BOARD_PACK: ReportDef = { key: 'board-pack', title: 'Board pack', what: 'All four reports in one PDF, with a short written summary at the front.', why: '' };

export const reportTitle = (key: ReportKey) => (REPORTS.find(r => r.key === key) ?? BOARD_PACK).title;
export const isReportKey = (key: string): key is ReportKey => key === BOARD_PACK.key || REPORTS.some(r => r.key === key);
export const reportUrl = (stock: Stock, key: ReportKey, totals = true) => `/registry-report/${stock.ticker}:${stock.exchange}/${key}${totals ? '' : '?totals=0'}`;

// "Reports you've made": there is no report history API yet, so these rows are sample-labelled preview rows (the
// page shows a Sample pill on each). Download opens the Download modal, which makes the report from today's register.
export interface MadeReport { key: ReportKey; title: string; sub: string; file: string }
export const SAMPLE_MADE: MadeReport[] = [
    { key: 'board-pack', title: 'Board pack', sub: 'made by you', file: 'PDF' },
    { key: 'top-20', title: 'Top 20 shareholders', sub: 'emailed automatically', file: 'PDF' },
    { key: 'who-bought-and-sold', title: 'Who bought and sold', sub: 'emailed automatically', file: 'Excel' },
    { key: 'holders-by-size', title: 'Holders by size', sub: 'emailed automatically', file: 'PDF' },
];

// ---- loading ----

export interface RegistryData { top: TopHoldersResult | null; listing: RegisterListingResult | null; error: string; loading: boolean }

// both GETs in parallel, like TopHolders.tsx; either may fail on its own (the reports that need it show "—")
export const useRegistryData = (stock: Stock | null | undefined): RegistryData => {
    const [data, setData] = useState<RegistryData>({ top: null, listing: null, error: '', loading: true });
    useAsyncEffect(async (isMounted: () => boolean) => {
        if (!stock) return;
        setData({ top: null, listing: null, error: '', loading: true });
        const [[topErr, top], [regErr, reg]] = await Promise.all([
            to(api().getTopHolders(stock.ticker, stock.exchange)),
            to(api().getRegisterListing(stock.ticker, stock.exchange)),
        ]);
        if (!isMounted()) return;
        const t = (top as TopHoldersResult | undefined)?.success ? top as TopHoldersResult : null;
        const l = (reg as RegisterListingResult | undefined)?.success ? reg as RegisterListingResult : null;
        const error = !t && !l ? ((topErr || regErr) as Error | null)?.message || (top as TopHoldersResult | undefined)?.message || 'Failed to load the register' : '';
        setData({ top: t, listing: l, error, loading: false });
    }, [stock?.id]);
    return data;
};

// ---- formatting ----

export const num = (n: number) => n.toLocaleString();
export const pct = (n: number, dp = 2) => `${n.toFixed(dp)}%`;
export const signed = (n: number, dp?: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${dp === undefined ? Math.abs(n).toLocaleString() : Math.abs(n).toFixed(dp)}`;
export const asAtDate = (listing: RegisterListingResult | null, format = 'D MMMM YYYY') => listing?.asAt ? moment(listing.asAt).format(format) : null;

// ---- calculations ----

const primaryIndex = (stock: Stock, listing: RegisterListingResult) => Math.max((listing.securityCodes || []).indexOf(stock.ticker), 0);
const balance = (h: RegisterListingHolder, i: number) => h.balances?.[i] ?? 0;
const positive = (stock: Stock, listing: RegisterListingResult | null) => {
    if (!listing) return [];
    const i = primaryIndex(stock, listing);
    return listing.holders.filter(h => balance(h, i) > 0);
};

// shares on issue: the provider's top-holders report total, else the sum of every balance on the register
export const sharesOnIssue = (stock: Stock, data: RegistryData) => {
    const fromTop = Number(data.top?.report?.totalIssuedCapital);
    if (fromTop > 0) return fromTop;
    if (!data.listing) return 0;
    const i = primaryIndex(stock, data.listing);
    return data.listing.holders.reduce((s, h) => s + balance(h, i), 0);
};

export const holderCount = (stock: Stock, listing: RegisterListingResult | null) =>
    listing ? listing.holderCount ?? positive(stock, listing).length : null;

export const emailCount = (stock: Stock, listing: RegisterListingResult | null) =>
    listing ? positive(stock, listing).filter(h => (h.email || '').trim()).length : null;

export interface TopRow { position: number; name: string; holderNumber: string; holding: number; pct: number; pts: number | null }

// positions 1..n from the provider report (tied holders share a position, so this can be more than n rows).
// pts = change in % held over the last 30 days, from each holder's 30-day-old balance over today's shares on issue.
export const topRows = (stock: Stock, data: RegistryData, n = 20): TopRow[] => {
    const issued = sharesOnIssue(stock, data);
    const listing = data.listing;
    const i = listing ? primaryIndex(stock, listing) : 0;
    const byId = new Map((listing?.holders || []).map(h => [h.holderId, balance(h, i) - (h.balanceHistorical ?? 0)]));
    const hasHistory = !!listing?.balanceHistoricalDate;
    const change = (ids: (number | null)[]) => {
        if (!hasHistory || !issued) return null;
        const known = ids.filter((id): id is number => !!id && byId.has(id));
        if (!known.length) return null;
        return known.reduce((s, id) => s + (byId.get(id) ?? 0), 0) / issued * 100;
    };
    return (data.top?.report?.holders || []).filter(h => h.position <= n).map(h => ({
        position: h.position,
        name: h.group ? h.groupName : (h.holderNames || []).join(' & '),
        holderNumber: h.holderNumber || '',
        holding: Number(h.holding),
        pct: Number(h.issuedCapitalPercentage) * 100,
        pts: change(h.group ? (h.groupMembers || []).map(m => m.holderId) : [h.holderId]),
    }));
};

export interface Band { label: string; holders: number; shares: number; holdersPct: number; sharesPct: number }
const BAND_LIMITS: [number, number, string][] = [
    [1, 1000, '1 – 1,000'], [1001, 5000, '1,001 – 5,000'], [5001, 10000, '5,001 – 10,000'],
    [10001, 100000, '10,001 – 100,000'], [100001, Infinity, '100,001 and over'],
];

// ASX distribution schedule, one register line per holder record
export const bands = (stock: Stock, listing: RegisterListingResult | null): Band[] | null => {
    if (!listing) return null;
    const i = primaryIndex(stock, listing);
    const holders = positive(stock, listing);
    const totalHolders = holders.length;
    const totalShares = holders.reduce((s, h) => s + balance(h, i), 0);
    return BAND_LIMITS.map(([lo, hi, label]) => {
        const inBand = holders.filter(h => balance(h, i) >= lo && balance(h, i) <= hi);
        const shares = inBand.reduce((s, h) => s + balance(h, i), 0);
        return {
            label, holders: inBand.length, shares,
            holdersPct: totalHolders ? inBand.length / totalHolders * 100 : 0,
            sharesPct: totalShares ? shares / totalShares * 100 : 0,
        };
    });
};

export type MoveKind = 'Increased' | 'Decreased' | 'New holder' | 'Left the register';
export interface Move { holderId: number; name: string; holderNumber: string; kind: MoveKind; change: number; now: number; pct: number }
export interface Moves { from: string; to: string | null; rows: Move[] }

// every holder whose balance differs from its 30-day-old snapshot; null until a snapshot that old exists
export const moves = (stock: Stock, data: RegistryData): Moves | null => {
    const listing = data.listing;
    if (!listing?.balanceHistoricalDate) return null;
    const i = primaryIndex(stock, listing);
    const issued = sharesOnIssue(stock, data);
    const rows = listing.holders.map((h): Move | null => {
        const now = balance(h, i);
        const before = h.balanceHistorical ?? 0;
        const change = now - before;
        if (!change) return null;
        let kind: MoveKind = change > 0 ? 'Increased' : 'Decreased';
        if (before === 0) kind = 'New holder';
        else if (now === 0) kind = 'Left the register';
        return { holderId: h.holderId, name: h.name, holderNumber: h.holderNumber, kind, change, now, pct: issued ? now / issued * 100 : 0 };
    }).filter((m): m is Move => !!m).sort((a, b) => Math.abs(b.change) - Math.abs(a.change));
    return { from: listing.balanceHistoricalDate, to: listing.asAt ?? null, rows };
};

export const moveCounts = (m: Moves) => {
    const up = m.rows.filter(r => r.change > 0 && r.kind !== 'New holder');
    const down = m.rows.filter(r => r.change < 0 && r.kind !== 'Left the register');
    return {
        up: up.length, upShares: up.reduce((s, r) => s + r.change, 0),
        down: down.length, downShares: down.reduce((s, r) => s + r.change, 0),
        joined: m.rows.filter(r => r.kind === 'New holder').length,
        left: m.rows.filter(r => r.kind === 'Left the register').length,
    };
};

export const substantial = (stock: Stock, data: RegistryData) => topRows(stock, data, Infinity).filter(r => r.pct >= 5);

// ---- CSV (Download modal) ----

const csvCell = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;

export const saveCsv = (fileName: string, lines: (string | number)[][]) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([lines.map(l => l.map(csvCell).join(',')).join('\r\n')], { type: 'text/csv' }));
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(a.href);
};

// contact columns come from the register listing, matched on holder id / number
export const reportCsv = (stock: Stock, data: RegistryData, key: ReportKey, contacts: boolean): (string | number)[][] | null => {
    const holders = new Map((data.listing?.holders || []).map(h => [h.holderNumber, h]));
    const contactHead = contacts ? ['Email', 'Address'] : [];
    const contactCells = (holderNumber: string) => {
        const h = holders.get(holderNumber);
        return contacts ? [h?.email || '', h?.address || ''] : [];
    };
    if (key === 'top-20' || key === 'substantial-holders') {
        const rows = key === 'top-20' ? topRows(stock, data) : substantial(stock, data);
        if (!data.top) return null;
        return [['#', 'Holder', 'Holder No.', 'Shares', '% Held', '30-day change (pts)', ...contactHead],
            ...rows.map(r => [r.position, r.name, r.holderNumber, r.holding, r.pct.toFixed(2), r.pts === null ? '' : r.pts.toFixed(2), ...contactCells(r.holderNumber)])];
    }
    if (key === 'holders-by-size') {
        const b = bands(stock, data.listing);
        if (!b) return null;
        return [['Holding range', 'Holders', '% of holders', 'Shares', '% of shares'],
            ...b.map(x => [x.label, x.holders, x.holdersPct.toFixed(1), x.shares, x.sharesPct.toFixed(2)])];
    }
    if (key === 'who-bought-and-sold') {
        const m = moves(stock, data);
        if (!m) return null;
        return [['Holder', 'Holder No.', 'What happened', 'Change', 'Now holds', '% Held', ...contactHead],
            ...m.rows.map(r => [r.name, r.holderNumber, r.kind, r.change, r.now, r.pct.toFixed(2), ...contactCells(r.holderNumber)])];
    }
    return null;
};
